import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useAppConfig } from "../context/AppConfigContext";
import { useTranslation } from "../i18n/LanguageContext";
import { Download, Share, X } from "lucide-react";
import {
  usePwaInstall,
  isSnoozed,
  recordDismiss,
  promptInstall,
  onInstallPopupRequest,
} from "../utils/pwaInstall";

// Shows on every page load while the app isn't installed. After 3 dismissals it
// snoozes for 3, then 7, then 14 days (see utils/pwaInstall.js). The sidebar
// "Install app" item can always open it.

export default function PWAInstallPrompt() {
  const { user } = useAuth();
  const { appName, branding } = useAppConfig();
  const { t } = useTranslation();
  const { canInstall, isIOS } = usePwaInstall();
  const [autoAllowed] = useState(() => !isSnoozed());
  const [openedByUser, setOpenedByUser] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [iconFailed, setIconFailed] = useState(false);

  // Inject /app manifest and register service worker (required for beforeinstallprompt)
  useEffect(() => {
    // Only on /app routes
    if (!window.location.pathname.startsWith("/app")) return;

    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/manifest.json";
    document.head.appendChild(link);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw-app.js", { scope: "/app/" }).catch(() => {});
    }

    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
    };
  }, []);

  useEffect(
    () =>
      onInstallPopupRequest(() => {
        setOpenedByUser(true);
        setDismissed(false);
      }),
    []
  );

  const handleInstall = useCallback(async () => {
    if (await promptInstall()) setDismissed(true);
  }, []);

  const handleDismiss = useCallback(() => {
    // Closing it after opening it from the sidebar doesn't count toward the snooze
    if (!openedByUser) recordDismiss();
    setOpenedByUser(false);
    setDismissed(true);
  }, [openedByUser]);

  if (!user || !canInstall || dismissed || !(autoAllowed || openedByUser)) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-white rounded-xl shadow-lg border border-gray-200 p-4 z-[60] animate-slide-up">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        {branding?.icon && !iconFailed ? (
          <img
            src={branding.icon}
            alt=""
            onError={() => setIconFailed(true)}
            className="flex-shrink-0 w-12 h-12 rounded-xl object-cover"
          />
        ) : (
          <div className="flex-shrink-0 bg-blue-50 rounded-lg p-2">
            <Download size={24} className="text-blue-600" />
          </div>
        )}
        <div className="flex-1 min-w-0 pr-4">
          <p className="text-sm font-semibold text-gray-900">
            {t("pwa.installTitle", { appName })}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {t("pwa.installDesc")}
          </p>
          {isIOS ? (
            <p className="text-xs text-gray-600 mt-2 flex items-center gap-1 flex-wrap">
              <Share size={14} className="text-blue-600 flex-shrink-0" />
              {t("pwa.iosInstructions")}
            </p>
          ) : (
            <button
              onClick={handleInstall}
              className="mt-2 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t("pwa.installButton")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
